import { useEffect, useState } from "react";
import { HelpHint } from "../components/HelpTooltip";
import { useSetTrail } from "../context/BreadcrumbContext";
import { api } from "../lib/api";
import type { LeaderboardEntry, LeaderboardFilter } from "../types/domain";

const filters: Array<{ key: LeaderboardFilter; label: string }> = [
  { key: "all", label: "Toutes" },
  { key: "quick", label: "Rapides" },
  { key: "tournament", label: "Tournoi" },
];

const filterInfo: Record<LeaderboardFilter, string> = {
  all: "Classement global sur toutes les parties jouées.",
  quick: "Classement basé sur les parties rapides uniquement.",
  tournament: "Classement basé sur les parties de tournoi uniquement.",
};

export function LeaderboardPage() {
  const [filter, setFilter] = useState<LeaderboardFilter>("all");
  const [rows, setRows] = useState<LeaderboardEntry[]>([]);

  useSetTrail([]);

  useEffect(() => {
    void api.listLeaderboard(filter).then(setRows);
  }, [filter]);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-ink">Leaderboard 🏆</h1>
        <p className="mt-1 text-sm text-smoke">
          Le score reflète la performance moyenne par partie. Plus vous jouez,
          plus votre classement est fiable.
        </p>
      </div>

      {/* Filter toggle */}
      <div className="toggle-group">
        {filters.map((f) => (
          <button
            key={f.key}
            type="button"
            data-active={filter === f.key ? "true" : "false"}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      <HelpHint text={filterInfo[filter]} />

      {/* Entries */}
      <div className="space-y-3">
        {rows.map((row, index) => (
          <div
            key={row.playerId}
            className="card flex items-center justify-between gap-3"
          >
            <div className="flex items-center gap-3">
              <span className="text-sm font-bold text-smoke w-5 text-right">
                {index === 0
                  ? "🥇"
                  : index === 1
                    ? "🥈"
                    : index === 2
                      ? "🥉"
                      : index + 1}
              </span>
              <div>
                <div className="font-semibold text-ink">{row.pseudo}</div>
                <div className="text-xs text-smoke">
                  diff {row.differential >= 0 ? "+" : ""}
                  {row.differential} · {row.gamesPlayed} partie
                  {row.gamesPlayed !== 1 ? "s" : ""}
                </div>
              </div>
            </div>
            <div className="text-right">
              <div
                className={`text-2xl font-bold ${
                  index === 0 && row.score > 0
                    ? "text-amber-500"
                    : row.score > 0
                      ? "text-ember"
                      : row.score < 0
                        ? "text-red-500"
                        : "text-smoke"
                }`}
              >
                {row.score >= 0 ? "+" : ""}
                {row.score}
              </div>
            </div>
          </div>
        ))}
        {rows.length === 0 && (
          <p className="text-sm text-smoke">
            Aucun joueur classé pour ce filtre.
          </p>
        )}
      </div>
    </div>
  );
}
